import React, { ReactNode, useMemo } from "react";
import { PageLocationContext } from "./PageLocationContext";

// Unwrap "heroItem" containers into their nested blockHero items.
// "heroItem" has no entry in BLOCK_MAP and cannot render directly via BlockRenderer.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const normalizeHero = (hero: any[]): any[] =>
    (hero || []).flatMap((item) => {
        const content = item.content;
        if (content?.alias === "heroItem") {
            // Unwrap heroItem: its children are already {content: blockHero} wrappers
            return (content.props?.heroes || []).filter(Boolean);
        }
        // Non-heroItem items: preserve the {content: ...} wrapper intact
        return [item];
    });

/**
 * Preview-only replacement for PageLocationProvider.
 * Reads globalThis.pageContent (server-injected draft data) synchronously —
 * no API fetch. All normalization of preview-specific data shapes lives here.
 */
export const PreviewPageLocationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const raw = (globalThis as any).pageContent;

    const pageData = useMemo(
        () =>
            raw
                ? {
                      ...raw,
                      props: raw.props
                          ? {
                                ...raw.props,
                                // Only normalize hero for non-frontpage pages: DA_HomePage handles
                                // heroItem wrappers natively, DA_GenericPage does not.
                                hero:
                                    raw.alias === "frontpage"
                                        ? raw.props.hero
                                        : normalizeHero(raw.props.hero),
                            }
                          : raw.props,
                  }
                : null,
        [raw]
    );

    const value = useMemo(
        () => ({
            displayedAtLocation: raw?.alias || raw?.pageAlias || null,
            isLoading: false,
            pageData,
        }),
        [raw, pageData]
    );

    return <PageLocationContext.Provider value={value}>{children}</PageLocationContext.Provider>;
};
